import React, { useMemo } from 'react';
import { CopyCard, CopyField, setStoreCopy } from './adminSiteCopyFields';
import { GIFT_FINDER_DEFAULTS } from '../content/giftFinderDefaults';
import { MAX_GIFT_GROUPS, MAX_GIFT_RECIPIENTS } from '../giftFinder/giftFinderGroups';
import {
  buildGiftGroupsFromCatalog,
  buildGiftGroupsFromProductTags,
  catalogGiftTagHealth,
  catalogSubcategoryHealth,
  SAMPLE_GIFT_RECIPIENTS,
} from './catalogHealthUtils';

function ToggleRow({ id, label, hint, checked, onChange }) {
  return (
    <div className="col-12">
      <div className="form-check form-switch">
        <input
          type="checkbox"
          className="form-check-input"
          id={id}
          role="switch"
          checked={Boolean(checked)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={id}>
          {label}
        </label>
        {hint ? <p className="small text-muted mb-0 mt-1">{hint}</p> : null}
      </div>
    </div>
  );
}

function slugListToText(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return list.join(', ');
}

function emptyGiftGroup(index) {
  return {
    id: '',
    label: '',
    hint: '',
    icon: '·',
    categories: '',
    subcategories: '',
    types: '',
    tags: '',
  };
}

function emptyRecipient(index) {
  return {
    id: '',
    label: '',
    hint: '',
    icon: '·',
    category: 'any',
    group: '',
    group_ids: '',
  };
}

function GiftGroupEditor({ groups, onChange }) {
  const list = groups.length ? groups : [emptyGiftGroup(0)];

  const setGroup = (index, patch) => {
    const next = [...list];
    const cur = { ...next[index], ...patch };
    next[index] = cur;
    onChange(next.slice(0, MAX_GIFT_GROUPS));
  };

  const addGroup = () => {
    if (list.length >= MAX_GIFT_GROUPS) return;
    onChange([...list, emptyGiftGroup(list.length)]);
  };

  const removeGroup = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  return (
    <>
      {list.map((g, i) => (
        <div key={`gf-group-${i}`} className="col-12 border rounded p-3 bg-light mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="small fw-semibold mb-0">Gift group {i + 1}</p>
            {list.length > 1 ? (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeGroup(i)}>
                Remove
              </button>
            ) : null}
          </div>
          <CopyField
            id={`gf-g-id-${i}`}
            label="Slug (id)"
            value={g.id || ''}
            onChange={(v) => setGroup(i, { id: v })}
            hint="Lowercase, e.g. kids, single, premium-under-500"
          />
          <CopyField id={`gf-g-label-${i}`} label="Label" value={g.label || ''} onChange={(v) => setGroup(i, { label: v })} />
          <CopyField id={`gf-g-hint-${i}`} label="Hint" value={g.hint || ''} onChange={(v) => setGroup(i, { hint: v })} />
          <CopyField id={`gf-g-icon-${i}`} label="Icon (emoji)" value={g.icon || ''} onChange={(v) => setGroup(i, { icon: v })} />
          <CopyField
            id={`gf-g-cat-${i}`}
            label="Collections (slugs)"
            value={typeof g.categories === 'string' ? g.categories : slugListToText(g.categories)}
            onChange={(v) => setGroup(i, { categories: v })}
            hint="Comma-separated category slugs, e.g. rakhi — leave blank for any collection."
          />
          <CopyField
            id={`gf-g-sub-${i}`}
            label="Subcategories (slugs)"
            value={typeof g.subcategories === 'string' ? g.subcategories : slugListToText(g.subcategories)}
            onChange={(v) => setGroup(i, { subcategories: v })}
            hint="Must match product Subcategory in Admin, e.g. kids, single, band"
          />
          <CopyField
            id={`gf-g-type-${i}`}
            label="Types (slugs)"
            value={typeof g.types === 'string' ? g.types : slugListToText(g.types)}
            onChange={(v) => setGroup(i, { types: v })}
            hint="Optional product type slugs — usually leave blank if you use subcategories only."
          />
          <CopyField
            id={`gf-g-tags-${i}`}
            label="Product tags (slugs)"
            value={typeof g.tags === 'string' ? g.tags : slugListToText(g.tags)}
            onChange={(v) => setGroup(i, { tags: v })}
            hint="Matches gift_tags on products — e.g. evil-eye, kids, wedding."
          />
        </div>
      ))}
      {list.length < MAX_GIFT_GROUPS ? (
        <div className="col-12">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addGroup}>
            + Add gift group
          </button>
        </div>
      ) : null}
    </>
  );
}

function RecipientEditor({ recipients, onChange }) {
  const list = recipients.length ? recipients : [emptyRecipient(0)];

  const setRec = (index, patch) => {
    const next = [...list];
    next[index] = { ...next[index], ...patch };
    onChange(next.slice(0, MAX_GIFT_RECIPIENTS));
  };

  const addRec = () => {
    if (list.length >= MAX_GIFT_RECIPIENTS) return;
    onChange([...list, emptyRecipient(list.length)]);
  };

  const removeRec = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  return (
    <>
      {list.map((r, i) => (
        <div key={`gf-rec-${i}`} className="col-12 border rounded p-3 bg-light mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="small fw-semibold mb-0">Recipient {i + 1}</p>
            {list.length > 1 ? (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRec(i)}>
                Remove
              </button>
            ) : null}
          </div>
          <CopyField
            id={`gf-r-id-${i}`}
            label="Slug (id)"
            value={r.id || ''}
            onChange={(v) => setRec(i, { id: v })}
            hint="e.g. brother, sister, bhabhi"
          />
          <CopyField id={`gf-r-label-${i}`} label="Label" value={r.label || ''} onChange={(v) => setRec(i, { label: v })} />
          <CopyField id={`gf-r-hint-${i}`} label="Hint" value={r.hint || ''} onChange={(v) => setRec(i, { hint: v })} />
          <CopyField id={`gf-r-icon-${i}`} label="Icon" value={r.icon || ''} onChange={(v) => setRec(i, { icon: v })} />
          <CopyField
            id={`gf-r-cat-${i}`}
            label="Preset collection"
            value={r.category || 'any'}
            onChange={(v) => setRec(i, { category: v })}
            hint="Category slug or any"
          />
          <CopyField
            id={`gf-r-group-${i}`}
            label="Preset gift group / type"
            value={r.group || ''}
            onChange={(v) => setRec(i, { group: v })}
            hint="Single group slug from gift groups above (optional)"
          />
          <CopyField
            id={`gf-r-groups-${i}`}
            label="Gift group ids (union)"
            value={typeof r.group_ids === 'string' ? r.group_ids : slugListToText(r.group_ids)}
            onChange={(v) => setRec(i, { group_ids: v })}
            hint="Comma-separated gift group slugs — products matching any listed group (optional)"
          />
        </div>
      ))}
      {list.length < MAX_GIFT_RECIPIENTS ? (
        <div className="col-12">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addRec}>
            + Add recipient
          </button>
        </div>
      ) : null}
    </>
  );
}

function giftGroupsForEditor(rawGroups) {
  return (rawGroups || []).map((g) => ({
    id: g.id || '',
    label: g.label || '',
    hint: g.hint || '',
    icon: g.icon || '·',
    categories: typeof g.categories === 'string' ? g.categories : slugListToText(g.categories),
    subcategories: typeof g.subcategories === 'string' ? g.subcategories : slugListToText(g.subcategories),
    types: typeof g.types === 'string' ? g.types : slugListToText(g.types),
    tags: typeof g.tags === 'string' ? g.tags : slugListToText(g.tags),
  }));
}

export default function AdminSiteGiftFinderFields({
  form,
  setField,
  catalogCategories = [],
  catalogProducts = {},
  setStatus,
}) {
  const store = form.store_copy || {};
  const gf = { ...GIFT_FINDER_DEFAULTS, ...(store.gift_finder || {}) };

  const setGiftFinder = (patch) => {
    setStoreCopy(form, setField, {
      gift_finder: { ...gf, ...patch },
    });
  };

  const giftGroups = Array.isArray(gf.gift_groups) ? gf.gift_groups : [];
  const recipients = Array.isArray(gf.recipients) ? gf.recipients : [];

  const catalogHealth = useMemo(
    () => catalogSubcategoryHealth(catalogProducts, catalogCategories),
    [catalogProducts, catalogCategories]
  );

  const tagHealth = useMemo(
    () => catalogGiftTagHealth(catalogProducts, catalogCategories),
    [catalogProducts, catalogCategories]
  );

  const importGiftGroupsFromCatalog = () => {
    const built = buildGiftGroupsFromCatalog(catalogCategories, catalogProducts);
    if (!built.length) {
      setStatus?.(
        'No gift groups built — set a real Subcategory on products (not blank or "all"), then try again.'
      );
      return;
    }
    setGiftFinder({
      gift_groups: giftGroupsForEditor(built),
      use_admin_gift_groups: true,
    });
    const base = `Imported ${built.length} gift group${built.length === 1 ? '' : 's'} from catalog. Save site settings to publish.`;
    if (catalogHealth.needsWork > 0) {
      setStatus?.(
        `${base} ${catalogHealth.needsWork} of ${catalogHealth.total} products still need subcategory — fix under Products.`
      );
    } else {
      setStatus?.(base);
    }
  };

  const importGiftGroupsFromTags = () => {
    const built = buildGiftGroupsFromProductTags(catalogCategories, catalogProducts);
    if (!built.length) {
      setStatus?.('No tags found — add Gift finder tags on products in Admin → Products, then import again.');
      return;
    }
    setGiftFinder({
      gift_groups: giftGroupsForEditor(built),
      use_admin_gift_groups: true,
      use_product_tags: true,
    });
    const base = `Imported ${built.length} gift group${built.length === 1 ? '' : 's'} from product tags. Save site settings to publish.`;
    if (tagHealth.untagged > 0) {
      setStatus?.(`${base} ${tagHealth.untagged} of ${tagHealth.total} products have no tags yet.`);
    } else {
      setStatus?.(base);
    }
  };

  const addSampleRecipients = () => {
    setGiftFinder({
      recipients: SAMPLE_GIFT_RECIPIENTS.map((r) => ({
        ...r,
        group_ids: typeof r.group_ids === 'string' ? r.group_ids : slugListToText(r.group_ids),
      })),
      show_recipient_step: true,
    });
    setStatus?.(
      'Added sample recipients — edit group_ids to match your imported gift group slugs, then save site settings.'
    );
  };

  return (
    <>
      <CopyCard
        title="Gift finder"
        subtitle="Guided product wizard at /gift-finder — turn off to hide all links and redirect visitors to Shop."
      >
        <ToggleRow
          id="gf-enabled"
          label="Enable gift finder"
          hint="When off, /gift-finder redirects to Shop and buttons disappear from the site."
          checked={gf.enabled}
          onChange={(v) => setGiftFinder({ enabled: v })}
        />
        <ToggleRow
          id="gf-home"
          label="Show button on home page"
          checked={gf.show_on_home}
          onChange={(v) => setGiftFinder({ show_on_home: v })}
        />
        <ToggleRow
          id="gf-shop"
          label="Show button on shop page"
          checked={gf.show_on_shop}
          onChange={(v) => setGiftFinder({ show_on_shop: v })}
        />
        <ToggleRow
          id="gf-nav"
          label="Show in Shop menu (header)"
          checked={gf.show_in_nav}
          onChange={(v) => setGiftFinder({ show_in_nav: v })}
        />
        <ToggleRow
          id="gf-footer"
          label="Show in footer"
          checked={gf.show_in_footer}
          onChange={(v) => setGiftFinder({ show_in_footer: v })}
        />
      </CopyCard>

      <CopyCard title="Gift finder — labels" subtitle="Button and page text">
        <CopyField
          id="gf-nav-label"
          label="Menu / footer link text"
          value={gf.nav_label || ''}
          onChange={(v) => setGiftFinder({ nav_label: v })}
        />
        <CopyField
          id="gf-home-label"
          label="Home page button"
          value={gf.home_button_label || ''}
          onChange={(v) => setGiftFinder({ home_button_label: v })}
        />
        <CopyField
          id="gf-shop-label"
          label="Shop page button"
          value={gf.shop_button_label || ''}
          onChange={(v) => setGiftFinder({ shop_button_label: v })}
        />
        <CopyField id="gf-eyebrow" label="Page eyebrow" value={gf.eyebrow || ''} onChange={(v) => setGiftFinder({ eyebrow: v })} />
        <CopyField id="gf-title" label="Page title" value={gf.title || ''} onChange={(v) => setGiftFinder({ title: v })} />
        <CopyField
          id="gf-subtitle"
          label="Page subtitle"
          value={gf.subtitle || ''}
          onChange={(v) => setGiftFinder({ subtitle: v })}
          multiline={3}
        />
        <CopyField
          id="gf-seo-title"
          label="Browser title (SEO)"
          value={gf.seo_title || ''}
          onChange={(v) => setGiftFinder({ seo_title: v })}
        />
        <CopyField
          id="gf-seo-desc"
          label="Meta description"
          value={gf.seo_description || ''}
          onChange={(v) => setGiftFinder({ seo_description: v })}
          multiline={2}
        />
      </CopyCard>

      <CopyCard title="Gift finder — steps" subtitle="Wizard flow and smart fallbacks">
        <ToggleRow
          id="gf-recipient-step"
          label='Include "Who is this for?" step'
          hint="Add recipients below — each can preset collection and gift groups."
          checked={gf.show_recipient_step}
          onChange={(v) => setGiftFinder({ show_recipient_step: v })}
        />
        <ToggleRow
          id="gf-use-admin-groups"
          label="Use admin gift groups on type step"
          hint="When on, the type step uses gift groups below. When off, uses catalog subcategories from products."
          checked={gf.use_admin_gift_groups}
          onChange={(v) => setGiftFinder({ use_admin_gift_groups: v })}
        />
        <ToggleRow
          id="gf-use-product-tags"
          label="Use product gift tags (v2)"
          hint="When admin groups are off, the type step lists tags from products. Groups below can also filter by tags."
          checked={gf.use_product_tags !== false}
          onChange={(v) => setGiftFinder({ use_product_tags: v })}
        />
        <ToggleRow
          id="gf-group-step"
          label='Include type / style step'
          hint="Skipped automatically when only one option exists."
          checked={gf.show_group_step}
          onChange={(v) => setGiftFinder({ show_group_step: v })}
        />
        <ToggleRow
          id="gf-budget-step"
          label="Include budget step"
          checked={gf.show_budget_step}
          onChange={(v) => setGiftFinder({ show_budget_step: v })}
        />
        <ToggleRow
          id="gf-auto-widen"
          label="Auto-widen sparse results"
          hint="If fewer than 3 products match, relax budget → type → recipient → collection automatically."
          checked={gf.auto_widen_results}
          onChange={(v) => setGiftFinder({ auto_widen_results: v })}
        />
        <CopyField
          id="gf-recipient-title"
          label="Recipient step heading"
          value={gf.recipient_step_title || ''}
          onChange={(v) => setGiftFinder({ recipient_step_title: v })}
        />
        <CopyField
          id="gf-recipient-lead"
          label="Recipient step lead"
          value={gf.recipient_step_lead || ''}
          onChange={(v) => setGiftFinder({ recipient_step_lead: v })}
          multiline={2}
        />
        <CopyField
          id="gf-collection-title"
          label="Collection step heading"
          value={gf.collection_step_title || ''}
          onChange={(v) => setGiftFinder({ collection_step_title: v })}
        />
        <CopyField
          id="gf-group-title"
          label="Type step heading"
          value={gf.group_step_title || ''}
          onChange={(v) => setGiftFinder({ group_step_title: v })}
        />
        <CopyField
          id="gf-budget-title"
          label="Budget step heading"
          value={gf.budget_step_title || ''}
          onChange={(v) => setGiftFinder({ budget_step_title: v })}
        />
        <CopyField
          id="gf-results-title"
          label="Results heading"
          value={gf.results_title || ''}
          onChange={(v) => setGiftFinder({ results_title: v })}
        />
        <CopyField
          id="gf-empty-msg"
          label="No results message"
          value={gf.results_empty_message || ''}
          onChange={(v) => setGiftFinder({ results_empty_message: v })}
          multiline={2}
        />
      </CopyCard>

      <CopyCard
        title="Gift groups"
        subtitle="Curated filters for the type step — map to product subcategories/types you use in Admin."
      >
        {catalogHealth.total > 0 && !catalogHealth.ok ? (
          <div className="col-12">
            <div className="alert alert-warning py-2 small mb-0" role="status">
              <strong>{catalogHealth.needsWork}</strong> of {catalogHealth.total} products still need subcategory
              before import is complete. Fix under <strong>Products</strong>, then re-import.
            </div>
          </div>
        ) : null}
        {tagHealth.total > 0 && tagHealth.untagged > 0 ? (
          <div className="col-12">
            <div className="alert alert-info py-2 small mb-0" role="status">
              <strong>{tagHealth.untagged}</strong> of {tagHealth.total} products have no gift tags — add tags under{' '}
              <strong>Products</strong> for richer gift finder steps.
            </div>
          </div>
        ) : null}
        <div className="col-12 d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={importGiftGroupsFromCatalog}>
            Import groups (subcategories)
          </button>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={importGiftGroupsFromTags}>
            Import groups (product tags)
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addSampleRecipients}>
            Add sample recipients
          </button>
        </div>
        <p className="col-12 small text-muted mb-0">
          Import reads subcategories from your live catalog draft. After import, turn on{' '}
          <strong>Use admin gift groups</strong> and save site settings.
        </p>
        <GiftGroupEditor
          groups={giftGroups}
          onChange={(next) => setGiftFinder({ gift_groups: next })}
        />
      </CopyCard>

      <CopyCard title="Recipients (optional)" subtitle="Who the gift is for — presets collection and groups">
        <RecipientEditor recipients={recipients} onChange={(next) => setGiftFinder({ recipients: next })} />
      </CopyCard>
    </>
  );
}
